import { addDays, format, startOfWeek } from 'date-fns';
import { uk } from 'date-fns/locale';

/** Формат, у якому бекенд приймає і віддає дати панелі. */
const API_DAY = 'yyyy-MM-dd';

export const API_DATETIME = 'yyyy-MM-dd HH:mm';

/** Межі сітки за замовчуванням. Реальні межі розширюються під наявні події. */
const HOUR_FROM = 8;

const HOUR_TO = 22;

/** Ліміт гостей на подію. Дзеркалить `StoreEventRequest::MAX_ATTENDEES` на бекенді. */
export const MAX_ATTENDEES = 20;

export const REPEAT_OPTIONS = [
  { value: 'none', label: 'Не повторюється' },
  { value: 'daily', label: 'Щодня' },
  { value: 'weekly', label: 'Щотижня' },
  { value: 'biweekly', label: 'Що два тижні' },
  { value: 'monthly', label: 'Щомісяця' },
];

export const REMINDER_OPTIONS = [
  { value: 5, label: 'За 5 хвилин' },
  { value: 15, label: 'За 15 хвилин' },
  { value: 30, label: 'За 30 хвилин' },
  { value: 60, label: 'За годину' },
  { value: 120, label: 'За 2 години' },
  { value: 180, label: 'За 3 години' },
  { value: 0, label: 'Без нагадування' },
];

/** Клас-модифікатор чипа за типом події. */
export const TYPE_MODIFIER = {
  lesson: 'lesson',
  trial: 'trial',
  catch_up: 'catchup',
  own: 'own',
};

export const weekStartOf = (date) => startOfWeek(date, { weekStartsOn: 1 });

export const weekDays = (start) => Array.from({ length: 7 }, (item, index) => addDays(start, index));

export const toApiDay = (date) => format(date, API_DAY);

export const dayKeyOf = (date) => format(date, API_DAY);

export const weekLabel = (start) => {
  const end = addDays(start, 6);
  const sameMonth = start.getMonth() === end.getMonth();

  return sameMonth
    ? `${format(start, 'd', { locale: uk })} – ${format(end, 'd MMMM yyyy', { locale: uk })}`
    : `${format(start, 'd MMM', { locale: uk })} – ${format(end, 'd MMM yyyy', { locale: uk })}`;
};

export const dayLabel = (date) => format(date, 'EEEE, d MMMM', { locale: uk });

export const shortWeekday = (date) => format(date, 'EEEEEE', { locale: uk });

export const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;

export const isSameDate = (first, second) => dayKeyOf(first) === dayKeyOf(second);

/**
 * Межі сітки: базові 8–22, але якщо подія раніше або пізніше — розсуваємо,
 * інакше вона просто зникла б із тижня.
 */
export const hoursRange = (events) => {
  let from = HOUR_FROM;
  let to = HOUR_TO;

  events.forEach((event) => {
    const hour = Number((event.start || '00:00').slice(0, 2));

    if (!Number.isNaN(hour)) {
      from = Math.min(from, hour);
      to = Math.max(to, hour);
    }
  });

  return Array.from({ length: to - from + 1 }, (item, index) => from + index);
};

/** Події у мапу `yyyy-MM-dd|H` — щоб пошук по клітинці був O(1). */
export const indexEvents = (events) => {
  const index = {};

  events.forEach((event) => {
    const hour = Number((event.start || '00:00').slice(0, 2));
    const key = `${event.date}|${hour}`;

    if (!index[key]) {
      index[key] = [];
    }

    index[key].push(event);
  });

  return index;
};

/** Дні тижня, у яких є хоч одна подія — для крапок під числами на мобілці. */
export const daysWithEvents = (events) => {
  const days = {};

  events.forEach((event) => {
    days[event.date] = true;
  });

  return days;
};

export const readErrorText = (error, fallback) => {
  const message = error?.response?.data?.info?.message;

  if (message) {
    return message;
  }

  const validation = error?.response?.data?.errors;

  if (validation) {
    const first = Object.values(validation)[0];

    if (Array.isArray(first) && first[0]) {
      return first[0];
    }
  }

  const code = error?.response?.status;

  return code ? `${fallback} (код ${code})` : fallback;
};

export const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());
